import { supabase } from '../lib/supabase';
import { TaskBoardTask } from '../types';

export interface SystemUser {
  id: string;
  email: string;
  app_role?: string;
  displayName: string;
}

export interface AssigneeOption {
  value: string;
  label: string;
  isSystemUser: boolean;
}

type RpcUserRow = {
  id?: string | null;
  email?: string | null;
  app_role?: string | null;
  role?: string | null;
};

export function systemUserDisplayName(email?: string | null): string {
  if (!email?.trim()) return 'Usuario';
  const local = email.split('@')[0] || email;
  return local
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export function mapRpcUserToSystemUser(raw: RpcUserRow): SystemUser | null {
  const id = raw.id?.trim();
  const email = raw.email?.trim();
  if (!id || !email) return null;

  return {
    id,
    email,
    app_role: raw.app_role ?? raw.role ?? undefined,
    displayName: systemUserDisplayName(email),
  };
}

export async function fetchSystemUsers(): Promise<SystemUser[]> {
  const { data, error } = await supabase.rpc('get_users_with_roles');
  if (error) {
    console.error('Error fetching system users:', error);
    return [];
  }
  return (data || [])
    .map((row: RpcUserRow) => mapRpcUserToSystemUser(row))
    .filter((u): u is SystemUser => u !== null);
}

export function getSystemUserByAssignee(
  assignee: string | null | undefined,
  users: SystemUser[]
): SystemUser | null {
  if (!assignee?.trim()) return null;
  const v = assignee.trim().toLowerCase();
  return users.find(u =>
    u.email.toLowerCase() === v ||
    u.displayName.toLowerCase() === v
  ) || null;
}

/** Convierte nombre/email de usuario del sistema al email canónico; texto libre se guarda tal cual. */
export function normalizeAssigneeInput(value: string, users: SystemUser[]): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const user = getSystemUserByAssignee(trimmed, users);
  if (user) return user.email;
  return trimmed;
}

export function formatAssigneeDisplay(
  assignee: string | null | undefined,
  users?: SystemUser[]
): string {
  if (!assignee?.trim()) return '';
  const user = users ? getSystemUserByAssignee(assignee, users) : null;
  if (user) return user.displayName;
  if (assignee.includes('@')) return systemUserDisplayName(assignee);
  return assignee;
}

/** Colores distintos por letra (A–Z) para avatares de responsable */
const LETTER_AVATAR_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#e11d48',
  '#be123c', '#9f1239', '#7c3aed', '#4f46e5', '#0891b2', '#059669',
  '#65a30d', '#ca8a04',
] as const;

export function getAssigneeInitialLetter(
  assignee: string | null | undefined,
  users?: SystemUser[]
): string {
  const display = formatAssigneeDisplay(assignee, users);
  const source = display.trim() || assignee?.trim() || '';
  return (source[0] || '?').toUpperCase();
}

export function getAssigneeLetterColor(letter: string): string {
  const code = letter.toUpperCase().charCodeAt(0);
  if (code >= 65 && code <= 90) {
    return LETTER_AVATAR_COLORS[code - 65];
  }
  return LETTER_AVATAR_COLORS[code % LETTER_AVATAR_COLORS.length];
}

export function getAssigneeAvatarStyle(
  assignee: string | null | undefined,
  users?: SystemUser[]
): { backgroundColor: string; color: string } {
  const letter = getAssigneeInitialLetter(assignee, users);
  const backgroundColor = getAssigneeLetterColor(letter);
  return { backgroundColor, color: '#ffffff' };
}

export function buildAssigneeFilterOptions(
  tasks: TaskBoardTask[],
  systemUsers: SystemUser[]
): AssigneeOption[] {
  const map = new Map<string, AssigneeOption>();

  for (const u of systemUsers) {
    map.set(u.email.toLowerCase(), {
      value: u.email,
      label: u.displayName,
      isSystemUser: true,
    });
  }

  for (const task of tasks) {
    const raw = task.assignee?.trim();
    if (!raw) continue;
    const systemUser = getSystemUserByAssignee(raw, systemUsers);
    const value = systemUser?.email ?? raw;
    const key = value.toLowerCase();
    if (!map.has(key)) {
      map.set(key, {
        value,
        label: systemUser?.displayName ?? raw,
        isSystemUser: !!systemUser,
      });
    }
  }

  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

export function isAssigneeForUser(
  assignee: string | null | undefined,
  userEmail?: string | null,
  userName?: string | null,
  systemUsers?: SystemUser[]
): boolean {
  if (!assignee?.trim()) return false;
  if (systemUsers?.length && userEmail) {
    const me = systemUsers.find(u => u.email.toLowerCase() === userEmail.toLowerCase());
    const assigned = getSystemUserByAssignee(assignee, systemUsers);
    if (me && assigned) return me.id === assigned.id;
  }
  const v = assignee.toLowerCase();
  const email = userEmail?.toLowerCase();
  const name = userName?.toLowerCase();
  if (email && v.includes(email)) return true;
  if (name && v.includes(name)) return true;
  return false;
}
